/**
 * useEnvironmentRuns — React Query hook for environment build runs.
 *
 * Live mode: fetches from API with conditional polling (3s when active runs exist).
 * Demo mode: returns static mock data for the shell UI.
 */


import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isDemoModeEnabled } from '../demo/demoMode';
import {
  fetchEnvironmentRuns,
  cancelEnvironmentRun,
  retryEnvironmentRun,
} from '../api/environmentRuns';
import type {
  EnvironmentRun,
  EnvironmentRunFilters,
  EnvironmentRunListResponse,
  EnvironmentRunStatus,
} from '../types/environmentRun';

// ── Demo data ────────────────────────────────────────────────────────────

const DEMO_RUNS: EnvironmentRun[] = [
  {
    run_id: 'env_run_001',
    theatre_id: 'tremor-1',
    construct_id: 'tremor-seismic-v3',
    status: 'COMPLETED',
    progress: 48,
    total_steps: 48,
    current_stage: null,
    modules_active: 0,
    modules_total: 4,
    artifacts_produced: 3,
    brier_score: 0.142,
    composite_score: 0.750,
    certificate_id: null,
    verification_level: 'CERTIFIED',
    receipt_hash: 'a3f2b1c4d5e6f708192a3b4c5d6e7f80a1b2c3d4e5f60718293a4b5c6d7e8f90',
    check_results_json: {
      construct_slug: 'tremor-seismic-v3',
      total_executed: 4,
      total_passed: 3,
      total_failed: 1,
      total_skipped: 0,
      has_critical_failures: false,
      checks: [
        { check_id: 'chk-001', check_type: 'SETTLEMENT_ACCURACY', status: 'PASSED', template_id: 'magnitude-gate', source_id: null, evidence: null, reason: null },
        { check_id: 'chk-002', check_type: 'ORACLE_CONSISTENCY', status: 'PASSED', template_id: null, source_id: null, evidence: null, reason: null },
        { check_id: 'chk-003', check_type: 'CALIBRATION_VALIDITY', status: 'PASSED', template_id: null, source_id: null, evidence: { computed_brier: 0.142 }, reason: null },
        { check_id: 'chk-004', check_type: 'FUNCTIONAL_CORRECTNESS', status: 'FAILED', template_id: null, source_id: null, evidence: null, reason: 'Module response timeout exceeded 5s threshold' },
      ],
    },
    evidence_hash: 'd4e5f6a7b8c9d0e1f2a3b4c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f70819',
    commitment_hash: 'e5f6a7b8c9d0e1f2a3b4c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f7081920',
    ground_truth_hash: 'f6a7b8c9d0e1f2a3b4c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f708192a3b',
    ground_truth_source: 'evaluation_contract',
    replay_count: 2,
    replay_verified: true,
    attestation_id: 'att_env_001',
    scoring_module_id: 'tremor-1',
    error: null,
    build_state_hash: null,
    started_at: '2026-03-21T14:22:00Z',
    finished_at: '2026-03-21T14:28:34Z',
    duration_ms: 394000,
    created_at: '2026-03-21T14:22:00Z',
    updated_at: '2026-03-21T14:28:34Z',
  },
  {
    run_id: 'env_run_002',
    theatre_id: 'tremor-1',
    construct_id: 'tremor-seismic-v3',
    status: 'COMPLETED',
    progress: 48,
    total_steps: 48,
    current_stage: null,
    modules_active: 0,
    modules_total: 4,
    artifacts_produced: 3,
    brier_score: 0.138,
    composite_score: 0.883,
    certificate_id: null,
    verification_level: 'PROVISIONAL',
    receipt_hash: 'b4c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f7081a2b3c4d5e6f7081a2b3c',
    check_results_json: null,
    evidence_hash: null,
    commitment_hash: null,
    ground_truth_hash: null,
    ground_truth_source: null,
    replay_count: null,
    replay_verified: null,
    attestation_id: null,
    scoring_module_id: null,
    error: null,
    build_state_hash: null,
    started_at: '2026-03-20T09:15:00Z',
    finished_at: '2026-03-20T09:21:12Z',
    duration_ms: 372000,
    created_at: '2026-03-20T09:15:00Z',
    updated_at: '2026-03-20T09:21:12Z',
  },
  {
    run_id: 'env_run_003',
    theatre_id: 'corona-1',
    construct_id: 'corona-space-weather-v1',
    status: 'FAILED',
    progress: 22,
    total_steps: 48,
    current_stage: 'EVALUATING',
    modules_active: 0,
    modules_total: 3,
    artifacts_produced: 1,
    brier_score: null,
    composite_score: null,
    certificate_id: null,
    verification_level: null,
    receipt_hash: null,
    check_results_json: null,
    evidence_hash: null,
    commitment_hash: null,
    ground_truth_hash: null,
    ground_truth_source: null,
    replay_count: null,
    replay_verified: null,
    attestation_id: null,
    scoring_module_id: null,
    error: 'Oracle timeout: NOAA SWPC feed returned 504 after 30s',
    build_state_hash: null,
    started_at: '2026-03-19T16:40:00Z',
    finished_at: '2026-03-19T16:45:18Z',
    duration_ms: 318000,
    created_at: '2026-03-19T16:40:00Z',
    updated_at: '2026-03-19T16:45:18Z',
  },
  {
    run_id: 'env_run_004',
    theatre_id: 'tremor-1',
    construct_id: 'tremor-seismic-v2',
    status: 'COMPLETED',
    progress: 36,
    total_steps: 36,
    current_stage: null,
    modules_active: 0,
    modules_total: 3,
    artifacts_produced: 2,
    brier_score: 0.167,
    composite_score: 0.812,
    certificate_id: null,
    verification_level: 'PROVISIONAL',
    receipt_hash: 'c5d6e7f8091a2b3c4d5e6f7081a2b3c4d5e6f7081a2b3c4d5e6f7081a2b3c4d',
    check_results_json: null,
    evidence_hash: null,
    commitment_hash: null,
    ground_truth_hash: null,
    ground_truth_source: null,
    replay_count: null,
    replay_verified: null,
    attestation_id: null,
    scoring_module_id: null,
    error: null,
    build_state_hash: null,
    started_at: '2026-03-18T11:05:00Z',
    finished_at: '2026-03-18T11:10:44Z',
    duration_ms: 344000,
    created_at: '2026-03-18T11:05:00Z',
    updated_at: '2026-03-18T11:10:44Z',
  },
];

const ACTIVE_STATUS_SET: Set<EnvironmentRunStatus> = new Set([
  'QUEUED',
  'INITIALISING',
  'INGESTING',
  'EVALUATING',
  'SETTLING',
  'CERTIFYING',
]);

// Stable empty reference
const EMPTY_RUNS: EnvironmentRun[] = [];

/** True for HTTP statuses that will not heal on retry (sprint-237: same
 * pattern as EnvironmentGraphPage's deterministic-error fail-fast —
 * `/environment/runs` is auth-GATED backend-side, so an unauthenticated
 * ambient viewer gets a deterministic 401). */
function isDeterministicHttpError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403 || status === 404;
}

/**
 * Fetch runs from the real API. Falls back to demo data ONLY when:
 *   1. Demo mode is explicitly enabled, OR
 *   2. The API is genuinely unavailable (network error, 5xx, auth failure)
 *      AND the caller has not opted out via `demoFallback: false`
 *
 * An empty successful response (0 runs) is real — NOT a fallback trigger.
 */
async function fetchWithDemoFallback(
  filters: EnvironmentRunFilters,
  forceDemo: boolean,
  demoFallback: boolean,
): Promise<EnvironmentRunListResponse> {
  if (forceDemo) return applyDemoFilters(filters);
  try {
    return await fetchEnvironmentRuns(filters);
  } catch (err: unknown) {
    // Only fall back to demo for infrastructure failures, not 4xx business errors
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status && status >= 400 && status < 500) {
      // Client errors (401, 403, 404, 422) — rethrow so React Query shows the real error
      throw err;
    }
    // Honest-data consumers (sprint-237 LineageTimelineStrip) opt out of the
    // demo degrade: a 500/network failure surfaces as an error, never fixtures.
    if (!demoFallback) throw err;
    // Network unreachable, 5xx, CORS, etc. — degrade to demo
    return applyDemoFilters(filters);
  }
}

function applyDemoFilters(filters: EnvironmentRunFilters): EnvironmentRunListResponse {
  let filtered: EnvironmentRun[] = DEMO_RUNS;
  if (filters.theatre_id) {
    filtered = filtered.filter((r) => r.theatre_id === filters.theatre_id);
  }
  if (filters.status) {
    filtered = filtered.filter((r) => r.status === filters.status);
  }
  if (filters.construct_id) {
    const q = filters.construct_id.toLowerCase();
    filtered = filtered.filter((r) =>
      r.construct_id.toLowerCase().includes(q),
    );
  }
  const total = filtered.length;
  const offset = filters.offset ?? 0;
  const limit = filters.limit ?? 20;
  const paged = filtered.slice(offset, offset + limit);
  return { runs: paged, total, limit, offset };
}

export function useEnvironmentRuns(
  filters: EnvironmentRunFilters = {},
  options?: { enabled?: boolean; demoFallback?: boolean },
) {
  const isDemo = isDemoModeEnabled();
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;
  const demoFallback = options?.demoFallback ?? true;

  const query = useQuery({
    queryKey: ['environmentRuns', filters, isDemo, demoFallback],
    queryFn: () => fetchWithDemoFallback(filters, isDemo, demoFallback),
    enabled,
    staleTime: 5000,
    // Deterministic 401/403/404 → fail fast into the strip's honest-empty
    // state (sprint-237; same bug class as the forge-rail stuck-load).
    // Transient errors keep a bounded retry.
    retry: (failureCount, error) =>
      isDeterministicHttpError(error) ? false : failureCount < 3,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs ?? [];
      const hasActive = runs.some((r) => ACTIVE_STATUS_SET.has(r.status));
      return hasActive ? 3000 : false;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (runId: string) => cancelEnvironmentRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environmentRuns'] });
      queryClient.invalidateQueries({ queryKey: ['environmentRunSteps'] });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (runId: string) => retryEnvironmentRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environmentRuns'] });
    },
  });

  return {
    runs: query.data?.runs ?? EMPTY_RUNS,
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    cancelRun: cancelMutation.mutateAsync,
    retryRun: retryMutation.mutateAsync,
    isCancelling: cancelMutation.isPending,
    isRetrying: retryMutation.isPending,
  };
}
